import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { communityApi, type CommunityActivityType } from '../../api/community.api';
import { usersApi, type PublicUser } from '../../api/users.api';
import type { CommunityTripSummary } from '../../types';
import { CommunityTripCard } from '../../components/community/CommunityTripCard';
import DestinationAutosuggest from '../../components/community/DestinationAutosuggest';
import Spinner from '../../components/ui/Spinner';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { UserAvatar } from '../../components/ui/UserAvatar';
import { toast } from '../../components/ui/Toast';
import { Compass, Icon, X } from '../../components/ui/Icon';
import EmptyState from '../../components/ui/EmptyState';

type Sort = 'recent' | 'top_rated' | 'most_cloned';

const SORT_KEYS: Record<Sort, string> = {
  recent:      'explore.sort.recent',
  top_rated:   'explore.sort.topRated',
  most_cloned: 'explore.sort.mostUsed',
};

const ACTIVITY_TYPES: CommunityActivityType[] = [
  'RESTAURANT', 'CAFE', 'MONUMENT', 'VIEWPOINT', 'NEIGHBORHOOD',
  'PARK', 'SHOP', 'EXPERIENCE', 'TRANSPORT', 'PARKING', 'BEACH', 'NATURE', 'ACCOMMODATION', 'OTHER',
];

const LIMIT = 20;

interface Filters {
  destination: string;
  minNights: string;
  maxNights: string;
  minBudgetPerDay: string;
  maxBudgetPerDay: string;
  activityTypes: CommunityActivityType[];
  startMonth: string;
  minParticipants: string;
  maxParticipants: string;
}

const EMPTY_FILTERS: Filters = {
  destination: '',
  minNights: '',
  maxNights: '',
  minBudgetPerDay: '',
  maxBudgetPerDay: '',
  activityTypes: [],
  startMonth: '',
  minParticipants: '',
  maxParticipants: '',
};

function activeFilterCount(f: Filters): number {
  let n = 0;
  if (f.destination) n++;
  if (f.minNights || f.maxNights) n++;
  if (f.minBudgetPerDay || f.maxBudgetPerDay) n++;
  if (f.activityTypes.length > 0) n++;
  if (f.startMonth) n++;
  if (f.minParticipants || f.maxParticipants) n++;
  return n;
}

function toApiParams(f: Filters) {
  return {
    destination:     f.destination.trim() || undefined,
    minNights:       f.minNights       !== '' ? Number(f.minNights)       : undefined,
    maxNights:       f.maxNights       !== '' ? Number(f.maxNights)       : undefined,
    minBudgetPerDay: f.minBudgetPerDay !== '' ? Number(f.minBudgetPerDay) : undefined,
    maxBudgetPerDay: f.maxBudgetPerDay !== '' ? Number(f.maxBudgetPerDay) : undefined,
    activityTypes:   f.activityTypes.length > 0 ? f.activityTypes : undefined,
    startMonth:      f.startMonth      !== '' ? Number(f.startMonth)      : undefined,
    minParticipants: f.minParticipants !== '' ? Number(f.minParticipants) : undefined,
    maxParticipants: f.maxParticipants !== '' ? Number(f.maxParticipants) : undefined,
  };
}

export default function ExplorePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [trips, setTrips]         = useState<CommunityTripSummary[]>([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset]       = useState(0);
  const [sort, setSort]           = useState<Sort>('top_rated');
  const [draftSort, setDraftSort] = useState<Sort>('top_rated');
  const [filters, setFilters]     = useState<Filters>(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState<Filters>(EMPTY_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [feed, setFeed]           = useState<'all' | 'following'>('all');
  const [following, setFollowing] = useState<PublicUser[]>([]);
  const [authorId, setAuthorId]   = useState<string | undefined>();

  async function load(
    s: Sort, f: Filters, feedVal: 'all' | 'following', aid: string | undefined, off: number, append = false,
  ) {
    if (!append) setLoading(true); else setLoadingMore(true);
    try {
      const data = await communityApi.list({
        sort: s,
        ...toApiParams(f),
        feed: feedVal === 'following' ? 'following' : undefined,
        authorId: aid,
        limit: LIMIT,
        offset: off,
      });
      setTrips(prev => append ? [...prev, ...data.trips] : data.trips);
      setTotal(data.total);
      setOffset(off);
    } catch {
      toast(t('explore.toast.loadFailed'), 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    load(sort, filters, feed, authorId, 0);
  }, [sort, feed, authorId, filters]);

  useEffect(() => {
    if (feed === 'following') {
      usersApi.getFollowingProfiles().then(setFollowing).catch(() => {});
    }
  }, [feed]);

  function handleFeedChange(f: 'all' | 'following') {
    setFeed(f);
    setAuthorId(undefined);
  }

  function toggleAuthor(id: string) {
    setAuthorId(prev => prev === id ? undefined : id);
  }

  function handleClone(tripId: string) {
    navigate(`/explore/${tripId}`);
  }

  function openDrawer() {
    setDraftFilters(filters);
    setDraftSort(sort);
    setDrawerOpen(true);
  }

  function applyDraftFilters() {
    setFilters(draftFilters);
    setSort(draftSort);
    setDrawerOpen(false);
  }

  function clearAllFilters() {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
  }

  function clearFilterChip(key: keyof Filters | 'nights' | 'budget' | 'participants') {
    setFilters(prev => {
      const next = { ...prev };
      if (key === 'nights')        { next.minNights = ''; next.maxNights = ''; }
      else if (key === 'budget')   { next.minBudgetPerDay = ''; next.maxBudgetPerDay = ''; }
      else if (key === 'participants') { next.minParticipants = ''; next.maxParticipants = ''; }
      else if (key === 'activityTypes') next.activityTypes = [];
      else if (key === 'destination')   next.destination   = '';
      else if (key === 'startMonth')    next.startMonth    = '';
      return next;
    });
  }

  const activeCount = useMemo(() => activeFilterCount(filters), [filters]);
  const hasMore = trips.length < total;

  // Active chip descriptors
  const chips: { key: 'destination' | 'nights' | 'budget' | 'activityTypes' | 'startMonth' | 'participants'; label: string }[] = [];
  if (filters.destination) chips.push({ key: 'destination', label: `${t('explore.filters.destination')}: ${filters.destination}` });
  if (filters.minNights || filters.maxNights) {
    const min = filters.minNights || '0';
    const max = filters.maxNights || '∞';
    chips.push({ key: 'nights', label: `${t('explore.filters.nights')}: ${min}–${max}` });
  }
  if (filters.minBudgetPerDay || filters.maxBudgetPerDay) {
    const min = filters.minBudgetPerDay || '0';
    const max = filters.maxBudgetPerDay || '∞';
    chips.push({ key: 'budget', label: `${t('explore.filters.budgetPerDay')}: ${min}–${max}` });
  }
  if (filters.activityTypes.length > 0) {
    chips.push({ key: 'activityTypes', label: `${t('explore.filters.activities')}: ${filters.activityTypes.length}` });
  }
  if (filters.startMonth) {
    const monthName = new Date(2000, Number(filters.startMonth) - 1, 1).toLocaleString(undefined, { month: 'long' });
    chips.push({ key: 'startMonth', label: `${t('explore.filters.month')}: ${monthName}` });
  }
  if (filters.minParticipants || filters.maxParticipants) {
    const min = filters.minParticipants || '1';
    const max = filters.maxParticipants || '∞';
    chips.push({ key: 'participants', label: `${t('explore.filters.participants')}: ${min}–${max}` });
  }

  return (
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="shrink-0 px-4 md:px-8 pt-5 pb-0 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-end justify-between gap-4 pb-4">
          <div>
            <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-0.5">
              {t('explore.breadcrumb')}
            </p>
            <h1 className="font-serif font-semibold text-2xl md:text-3xl text-slate-900 dark:text-slate-100">
              {t('explore.heading')}
            </h1>
          </div>

          {/* Filters button */}
          <button
            onClick={openDrawer}
            className={`h-9 px-4 rounded-xl border text-sm font-medium transition-colors cursor-pointer shrink-0 flex items-center gap-1.5 ${
              activeCount > 0
                ? 'border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'
            }`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="7" y1="12" x2="17" y2="12" />
              <line x1="10" y1="18" x2="14" y2="18" />
            </svg>
            {t('explore.filters.button')}
            {activeCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-teal-600 text-white text-[10px] font-mono font-semibold">
                {activeCount}
              </span>
            )}
          </button>
        </div>

        {/* Feed toggle */}
        <div className="flex items-center gap-4 pb-3">
          <div className="flex items-center gap-1.5">
            {(['all', 'following'] as const).map(f => (
              <button
                key={f}
                onClick={() => handleFeedChange(f)}
                className={`h-7 px-3 rounded-full font-mono text-[10px] font-semibold tracking-wide transition-colors cursor-pointer ${
                  feed === f
                    ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                {f === 'all' ? t('explore.feed.all') : t('explore.feed.following')}
              </button>
            ))}
          </div>
        </div>

        {/* Active filter chips */}
        {chips.length > 0 && (
          <div className="flex items-center gap-2 pb-3 overflow-x-auto scrollbar-none">
            {chips.map(chip => (
              <button
                key={chip.key}
                onClick={() => clearFilterChip(chip.key)}
                className="flex items-center gap-1.5 h-7 pl-3 pr-2 rounded-full bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 text-teal-700 dark:text-teal-300 text-xs font-medium hover:bg-teal-100 dark:hover:bg-teal-900/40 transition-colors cursor-pointer shrink-0"
              >
                {chip.label}
                <Icon as={X} size={12} />
              </button>
            ))}
            <button
              onClick={clearAllFilters}
              className="font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors cursor-pointer shrink-0 px-2"
            >
              {t('explore.filters.clearAll')}
            </button>
          </div>
        )}

        {/* Following people filter */}
        {feed === 'following' && following.length > 0 && (
          <div className="flex items-center gap-2 pb-3 overflow-x-auto scrollbar-none">
            {following.map(user => {
              const name = user.displayName || user.username;
              const active = authorId === user.id;
              return (
                <button
                  key={user.id}
                  onClick={() => toggleAuthor(user.id)}
                  className={`flex items-center gap-1.5 h-7 pl-1 pr-3 rounded-full text-[10px] font-mono font-semibold shrink-0 transition-colors cursor-pointer border ${
                    active
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'
                      : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:text-slate-800 dark:hover:text-slate-200'
                  }`}
                >
                  <UserAvatar name={name} avatarUrl={user.avatarUrl} size={20} />
                  {name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 md:px-8 py-5 md:py-6">
        {loading ? (
          <LoadingScreen />
        ) : (
          <>
            <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-4">
              {t('explore.results.trips', { count: total })}
            </p>

            {trips.length === 0 ? (
              <EmptyState icon={Compass} title={t('explore.empty.title')} body={t('explore.empty.body')} />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {trips.map(trip => (
                  <CommunityTripCard
                    key={trip.id}
                    trip={trip}
                    onClone={handleClone}
                    cloning={false}
                  />
                ))}
              </div>
            )}

            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => load(sort, filters, feed, authorId, offset + LIMIT, true)}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {loadingMore ? <Spinner size="sm" /> : t('explore.loadMore')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Filter drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm animate-fade-in"
          onClick={() => setDrawerOpen(false)}
        >
          <div
            className="bg-white dark:bg-slate-900 w-full max-w-md h-full shadow-xl flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
              <h2 className="font-serif font-medium text-xl text-slate-900 dark:text-slate-100">
                {t('explore.filters.title')}
              </h2>
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
              >
                <Icon as={X} size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Sort */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                  {t('explore.filters.sortBy')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {(Object.keys(SORT_KEYS) as Sort[]).map(s => {
                    const active = draftSort === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setDraftSort(s)}
                        className={`h-8 px-3 rounded-full text-xs font-medium transition-colors cursor-pointer border ${
                          active
                            ? 'bg-teal-600 dark:bg-teal-500 text-white border-teal-600 dark:border-teal-500'
                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-300'
                        }`}
                      >
                        {t(SORT_KEYS[s])}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Destination */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                  {t('explore.filters.destination')}
                </label>
                <DestinationAutosuggest
                  value={draftFilters.destination}
                  onChange={v => setDraftFilters({ ...draftFilters, destination: v })}
                  placeholder={t('explore.filters.destinationPlaceholder')}
                />
              </div>

              {/* Nights range */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                  {t('explore.filters.nights')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number" min="0" inputMode="numeric"
                    value={draftFilters.minNights}
                    onChange={e => setDraftFilters({ ...draftFilters, minNights: e.target.value })}
                    placeholder={t('explore.filters.min')}
                    className="h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm outline-none focus:border-teal-400"
                  />
                  <input
                    type="number" min="0" inputMode="numeric"
                    value={draftFilters.maxNights}
                    onChange={e => setDraftFilters({ ...draftFilters, maxNights: e.target.value })}
                    placeholder={t('explore.filters.max')}
                    className="h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm outline-none focus:border-teal-400"
                  />
                </div>
              </div>

              {/* Budget per day range */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                  {t('explore.filters.budgetPerDay')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number" min="0" inputMode="numeric"
                    value={draftFilters.minBudgetPerDay}
                    onChange={e => setDraftFilters({ ...draftFilters, minBudgetPerDay: e.target.value })}
                    placeholder={t('explore.filters.min')}
                    className="h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm outline-none focus:border-teal-400"
                  />
                  <input
                    type="number" min="0" inputMode="numeric"
                    value={draftFilters.maxBudgetPerDay}
                    onChange={e => setDraftFilters({ ...draftFilters, maxBudgetPerDay: e.target.value })}
                    placeholder={t('explore.filters.max')}
                    className="h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm outline-none focus:border-teal-400"
                  />
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5 font-mono">{t('explore.filters.budgetHint')}</p>
              </div>

              {/* Activity categories */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                  {t('explore.filters.activities')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {ACTIVITY_TYPES.map(type => {
                    const active = draftFilters.activityTypes.includes(type);
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setDraftFilters(d => ({
                          ...d,
                          activityTypes: active
                            ? d.activityTypes.filter(x => x !== type)
                            : [...d.activityTypes, type],
                        }))}
                        className={`h-7 px-3 rounded-full text-xs font-medium transition-colors cursor-pointer border ${
                          active
                            ? 'bg-teal-600 dark:bg-teal-500 text-white border-teal-600 dark:border-teal-500'
                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-300'
                        }`}
                      >
                        {t(`explore.filters.activityType.${type}`)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Month */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                  {t('explore.filters.month')}
                </label>
                <select
                  value={draftFilters.startMonth}
                  onChange={e => setDraftFilters({ ...draftFilters, startMonth: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm outline-none focus:border-teal-400 cursor-pointer"
                >
                  <option value="">{t('explore.filters.anyMonth')}</option>
                  {Array.from({ length: 12 }).map((_, i) => {
                    const m = i + 1;
                    const name = new Date(2000, i, 1).toLocaleString(undefined, { month: 'long' });
                    return <option key={m} value={m}>{name}</option>;
                  })}
                </select>
              </div>

              {/* Participants range */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                  {t('explore.filters.participants')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number" min="1" inputMode="numeric"
                    value={draftFilters.minParticipants}
                    onChange={e => setDraftFilters({ ...draftFilters, minParticipants: e.target.value })}
                    placeholder={t('explore.filters.min')}
                    className="h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm outline-none focus:border-teal-400"
                  />
                  <input
                    type="number" min="1" inputMode="numeric"
                    value={draftFilters.maxParticipants}
                    onChange={e => setDraftFilters({ ...draftFilters, maxParticipants: e.target.value })}
                    placeholder={t('explore.filters.max')}
                    className="h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm outline-none focus:border-teal-400"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="shrink-0 flex items-center gap-3 px-6 py-4 border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => setDraftFilters(EMPTY_FILTERS)}
                className="text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors cursor-pointer"
              >
                {t('explore.filters.reset')}
              </button>
              <div className="flex-1" />
              <button
                onClick={() => setDrawerOpen(false)}
                className="h-10 px-4 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={applyDraftFilters}
                className="h-10 px-5 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-semibold hover:bg-slate-700 dark:hover:bg-slate-100 transition-colors cursor-pointer"
              >
                {t('explore.filters.apply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
